/**
 * Nome dell'app (DEC-15): unica fonte `Person.display_form`, letto dall'API.
 * Finche lei non ha scelto come farsi chiamare (WQ-B1-1) l'app si chiama solo "mia".
 * Da qui escono `<title>`, `name`/`short_name` del manifest e "Chiedi a mia …".
 */

export const DEFAULT_APP_NAME = 'mia'

export function appName(displayForm: string | null | undefined): string {
  const forma = (displayForm ?? '').trim()
  return forma ? `${DEFAULT_APP_NAME} ${forma}` : DEFAULT_APP_NAME
}

export function askAgentLabel(displayForm: string | null | undefined): string {
  return `Chiedi a ${appName(displayForm)}`
}

type Manifest = Record<string, unknown> & { icons?: { src: string }[] }

/**
 * Manifest con il nome di lei. Il manifest runtime vive in un blob: i percorsi relativi
 * (start_url, scope, icone) diventano assoluti, altrimenti si risolverebbero contro `blob:`.
 */
export function manifestFor(base: Manifest, displayForm: string | null | undefined, origin: string): Manifest {
  const nome = appName(displayForm)
  const assoluto = (percorso: unknown) => (typeof percorso === 'string' ? new URL(percorso, origin).href : percorso)
  return {
    ...base,
    name: nome,
    short_name: nome,
    start_url: assoluto(base.start_url),
    scope: assoluto(base.scope),
    icons: base.icons?.map((icona) => ({ ...icona, src: assoluto(icona.src) as string })),
  }
}

const MANIFEST_PATH = '/manifest.webmanifest'
let baseManifest: Promise<Manifest | null> | null = null
let manifestUrl: string | null = null

/** Aggiorna titolo e manifest nella pagina. Se il manifest non si legge, resta quello statico. */
export async function applyAppName(displayForm: string | null | undefined): Promise<void> {
  document.title = appName(displayForm)
  const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  if (!link) return
  baseManifest ??= fetch(MANIFEST_PATH)
    .then((risposta) => (risposta.ok ? (risposta.json() as Promise<Manifest>) : null))
    .catch(() => null)
  const base = await baseManifest
  if (!base) return
  const blob = new Blob([JSON.stringify(manifestFor(base, displayForm, window.location.origin))], {
    type: 'application/manifest+json',
  })
  if (manifestUrl) URL.revokeObjectURL(manifestUrl)
  manifestUrl = URL.createObjectURL(blob)
  link.href = manifestUrl
}
